import {
  getActivationStatus,
  updateActivationStatus,
  type ActivationStatusResponse,
  type UpdateActivationResponse,
} from 'api/v2/accountable-banking/activation';
import useQueryClient from 'hooks/react-query/useClientQuery';
import useMutation from 'hooks/react-query/useMutation';
import useQuery from 'hooks/react-query/useQuery';
import { getStoredAuthData } from 'utils/auth';
import type { BackendActivationStepIdType } from '../../components/pages/Banks/BankingSignUp/ActivationFlow/constants/stepIds';
import useHasAccountableBanking from './useHasAccountableBanking';

/**
 * React Query key for activation status cache
 */
export const ACTIVATION_STATUS_QUERY_KEY = 'accountable-banking-activation';

/**
 * Hook to fetch the current activation status
 * Returns the furthest step the user has reached in the activation flow
 * Returns null if user hasn't started activation
 */
export const useGetActivationStatus = () => {
  // Only fetch activation status when user is authenticated
  // This prevents API calls on login page that cause reload loops
  const accessToken = getStoredAuthData('access_token');
  const isAuthenticated = !!accessToken;
  const hasAccountableBanking = useHasAccountableBanking();

  return useQuery<ActivationStatusResponse, Error>(
    ACTIVATION_STATUS_QUERY_KEY,
    getActivationStatus,
    {
      enabled: Boolean(isAuthenticated && hasAccountableBanking),
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  );
};

/**
 * Hook to update the activation status
 * Updates the furthest step reached by the user
 * Backend only accepts forward progress (silently ignores backward updates)
 * Invalidates the activation status cache on success
 */
export const useUpdateActivationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateActivationResponse,
    Error,
    Exclude<BackendActivationStepIdType, null>
  >(
    (step: Exclude<BackendActivationStepIdType, null>) =>
      updateActivationStatus(step),
    {
      onSuccess: () => {
        // Invalidate activation status cache to refetch latest state
        queryClient.invalidateQueries(ACTIVATION_STATUS_QUERY_KEY);
      },
    },
  );
};
